import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, filter, map, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { FunctionRegistryService } from '../../../services/function-registry/function-registry.service';
import { HttpClient } from '@angular/common/http';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environment/environment';
import { EducationalPathDTO } from '../../../dto/educational-path/educational-path.dto';
import { EducationalPathVersionDTO } from '../../../dto/educational-path/educational-path-version.dto';
import {
  computeMaintenanceRollBounds,
  formatRollYearsLabel,
  isMultiYearRoll,
  MaintenanceRollRecurrenceInput,
  profileYearToReferenceDate,
  versionToRollInput,
} from '../../../utils/maintenance-roll-bounds.util';

interface MaintenanceItem {
  id: number;
  module_id?: number | null;
  subtype_id?: number | null;
  name?: string;
  itemName?: string;
  type: string;
  itemType?: string;
  start_date?: string | null;
  end_date?: string | null;
  maintenance_minutes?: number;
  required?: boolean;
  is_inherited?: boolean;
  inherited_from_path_name?: string | null;
  inherited_from_version_name?: string | null;
  /** Plain text from item_details (descriptions / description), for header line */
  itemDescription?: string;
}

interface RequirementItem {
  id: number;
  module_id?: number | null;
  subtype_id?: number | null;
  name?: string;
  type: string;
  start_date?: string | null;
  end_date?: string | null;
  itemDescription?: string;
}

/** A year pill on the coverage bar (internal, before compacting). */
export interface CoverageYear {
  year: number;
  covered: boolean;
  isCurrent: boolean;
}

/** Compact coverage chip: a single year or a consecutive range. */
export interface CoverageSegment {
  key: string;
  /** e.g. "2026" or "2020–2025" */
  label: string;
  covered: boolean;
  /** The year selected in the year filter. */
  isCurrent: boolean;
  /** The real calendar year (today). */
  isCalendarYear: boolean;
  title: string;
  startYear: number;
  endYear: number;
}

/** All entries for one unique module/subtype, with coverage analysis. */
export interface ModuleGroup<T> {
  /** Unique key = name for grouping */
  key: string;
  name: string;
  type: string;
  itemType?: string;
  /** The underlying module_id or subtype_id (from the first entry) – needed for inline add */
  sourceId: number | null;
  isSubtype: boolean;
  entries: T[];
  hasCurrentPeriodCoverage: boolean;
  coverageDisplay: CoverageSegment[];
}

@Component({
  selector: 'app-duna-educational-path-modules',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './duna-educational-path-modules.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-educational-path-modules.component.scss',
})
export class DunaEducationalPathModulesComponent implements OnInit, OnDestroy {
  maintenanceItems: MaintenanceItem[] = [];
  requirementItems: RequirementItem[] = [];
  loadingMaintenance = false;
  loadingRequirements = false;
  versionId: string | null = null;
  versionName: string | null = null;
  inheritsMaintenanceFromPathName: string | null = null;
  inheritsMaintenanceFromVersionName: string | null = null;
  currentPeriodStart: Date | null = null;
  currentPeriodEnd: Date | null = null;

  /** Calendar year for which module periods are shown (maintenance + requirements). */
  selectedModuleYear = new Date().getFullYear();

  private _filterActiveOnly = false;
  get filterActiveOnly(): boolean {
    return this._filterActiveOnly;
  }
  set filterActiveOnly(v: boolean) {
    if (this._filterActiveOnly !== v) {
      this._filterActiveOnly = v;
      this._maintenanceDirty = true;
      this._requirementsDirty = true;
    }
  }

  // Search
  maintenanceSearch = '';
  requirementsSearch = '';

  // Client-side pagination for group list (each tab)
  readonly maintenanceGroupsPageSize = 25;
  maintenanceGroupsPageIndex = 0;
  readonly requirementsGroupsPageSize = 25;
  requirementsGroupsPageIndex = 0;

  // Expand/collapse — keys of expanded cards
  expandedMaintenance = new Set<string>();
  expandedRequirements = new Set<string>();

  private destroy$ = new Subject<void>();
  private _maintenanceCache: ModuleGroup<MaintenanceItem>[] = [];
  private _requirementsCache: ModuleGroup<RequirementItem>[] = [];
  private _maintenanceDirty = true;
  private _requirementsDirty = true;
  private versionRecurrenceInput: MaintenanceRollRecurrenceInput | null = null;

  // Inline edit state
  editingMaintenanceId: number | null = null;
  editingRequirementId: number | null = null;
  editForm: { start_date: string; end_date: string; required: boolean } = {
    start_date: '',
    end_date: '',
    required: true,
  };
  editRequirementForm: { start_date: string; end_date: string } = {
    start_date: '',
    end_date: '',
  };

  // Inline "add period for this module" state
  addingPeriodForKey: string | null = null;
  addingReqPeriodForKey: string | null = null;
  inlineAddForm: { start_date: string; end_date: string; required: boolean } = {
    start_date: '',
    end_date: '',
    required: true,
  };
  inlineAddReqForm: { start_date: string; end_date: string } = {
    start_date: '',
    end_date: '',
  };
  savingInlineAdd = false;
  savingInlineAddReq = false;

  constructor(
    private componentDataSourceService: ComponentDataSourceService,
    private functionRegistryService: FunctionRegistryService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.componentDataSourceService
      .getCurrentVersionId$()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((id) => {
          this.versionId = id;
          this.maintenanceItems = [];
          this.requirementItems = [];
          this.currentPeriodStart = null;
          this.currentPeriodEnd = null;
          this.versionName = null;
          this.versionRecurrenceInput = null;
          this.selectedModuleYear = new Date().getFullYear();
          this._maintenanceDirty = true;
          this._requirementsDirty = true;
          this.cdr.markForCheck();
          if (!id) return of({ maintenance: [], requirements: [] });
          this.loadingMaintenance = true;
          this.loadingRequirements = true;
          this.cdr.markForCheck();
          const base = `${environment.apiUrl}/paths/versions/${id}`;
          const allParams = {
            params: { pageSize: '500', page: '1' },
            withCredentials: true,
          };
          return this.http.get<any>(`${base}/maintenance`, allParams).pipe(
            switchMap((maintenanceRes) => {
              const maintenance =
                this.normalizeMaintenanceResponse(maintenanceRes);
              return this.http
                .get<any>(`${base}/requirements`, {
                  params: { pageSize: '500', page: '1' },
                  withCredentials: true,
                })
                .pipe(
                  map((requirementsRes) => ({
                    maintenance,
                    requirements:
                      this.normalizeRequirementsResponse(requirementsRes),
                  }))
                );
            }),
            catchError((err) => {
              console.error('Failed to load modules', err);
              return of({ maintenance: [], requirements: [] });
            })
          );
        })
      )
      .subscribe(({ maintenance, requirements }) => {
        this.maintenanceItems = maintenance;
        this.requirementItems = requirements;
        this._maintenanceDirty = true;
        this._requirementsDirty = true;
        this.loadingMaintenance = false;
        this.loadingRequirements = false;
        this.cdr.markForCheck();
      });

    this.componentDataSourceService
      .getEducationalPathData()
      .pipe(
        takeUntil(this.destroy$),
        filter(Boolean),
        map((path: EducationalPathDTO | null) => this.getSelectedVersion(path))
      )
      .subscribe((version: EducationalPathVersionDTO | null) => {
        if (
          !version ||
          !this.versionId ||
          String(version.version_id) !== this.versionId
        ) {
          this.currentPeriodStart = null;
          this.currentPeriodEnd = null;
          this.versionName = null;
          this.inheritsMaintenanceFromPathName = null;
          this.inheritsMaintenanceFromVersionName = null;
          this.versionRecurrenceInput = null;
        } else {
          this.versionName = version.name ?? null;
          this.inheritsMaintenanceFromPathName =
            version.inherits_maintenance_from_path_name ?? null;
          this.inheritsMaintenanceFromVersionName =
            version.inherits_maintenance_from_version_name ?? null;
          this.versionRecurrenceInput = versionToRollInput(version);
          this.syncViewRollBounds();
        }
        this._maintenanceDirty = true;
        this._requirementsDirty = true;
        this.cdr.markForCheck();
      });

    this.functionRegistryService.registerFunction(
      'refreshEducationalPathModules',
      () => {
        if (!this.destroy$.closed) {
          this.loadMaintenance();
          this.loadRequirements();
        }
      }
    );
  }

  private getSelectedVersion(
    path: EducationalPathDTO | null
  ): EducationalPathVersionDTO | null {
    if (!path?.versions?.length || !this.versionId) return null;
    const vid = parseInt(this.versionId, 10);
    return path.versions.find((v) => v.version_id === vid) ?? null;
  }

  private normalizeMaintenanceResponse(result: any): MaintenanceItem[] {
    let items: any[] = [];
    if (result?.data && Array.isArray(result.data)) items = result.data;
    else if (result?.items && Array.isArray(result.items)) items = result.items;
    const translateType = (t: string) =>
      t === 'module'
        ? 'Modul'
        : t === 'subtype'
        ? 'Indholdstema'
        : t || 'Modul';
    return items.map((item: any) => {
      const name = item.item_details?.name || item.name || 'Unnamed';
      const isSubtype = !!item.subtype_id;
      const type = isSubtype ? 'Subtype' : 'Module';
      return {
        id: item.id,
        module_id: item.module_id ?? null,
        subtype_id: item.subtype_id ?? null,
        name,
        itemName: name,
        type,
        itemType: translateType(type.toLowerCase()),
        start_date: item.start_date ?? null,
        end_date: item.end_date ?? null,
        maintenance_minutes:
          item.version_details?.maintenance_minutes ??
          item.maintenance_minutes ??
          item.item_details?.maintenance_minutes ??
          undefined,
        required: item.required !== undefined ? item.required : true,
        itemDescription:
          this.extractItemDescriptionFromApi(item.version_details) ??
          this.extractItemDescriptionFromApi(item.item_details),
        is_inherited: !!(item.is_inherited ?? item.isInherited),
        inherited_from_path_name:
          item.inherited_from_path_name ?? item.inheritedFromPathName ?? null,
        inherited_from_version_name:
          item.inherited_from_version_name ??
          item.inheritedFromVersionName ??
          null,
      };
    });
  }

  isInheritedMaintenance(row: MaintenanceItem): boolean {
    return !!row.is_inherited;
  }

  groupHasInheritedEntries(group: ModuleGroup<MaintenanceItem>): boolean {
    return group.entries.some((e) => this.isInheritedMaintenance(e));
  }

  isInheritedOnlyGroup(group: ModuleGroup<MaintenanceItem>): boolean {
    return (
      group.entries.length > 0 &&
      group.entries.every((e) => this.isInheritedMaintenance(e))
    );
  }

  inheritedMaintenanceLabel(row: MaintenanceItem): string | null {
    if (!row.is_inherited) {
      return null;
    }
    const path = row.inherited_from_path_name?.trim();
    const ver = row.inherited_from_version_name?.trim();
    if (path && ver) {
      return `Arvet fra ${path} (${ver})`;
    }
    if (path) {
      return `Arvet fra ${path}`;
    }
    return 'Arvet modul';
  }

  groupInheritedSourceLabel(
    group: ModuleGroup<MaintenanceItem>
  ): string | null {
    const inherited = group.entries.find((e) => this.isInheritedMaintenance(e));
    return inherited ? this.inheritedMaintenanceLabel(inherited) : null;
  }

  showInheritedMaintenanceSectionHeader(index: number): boolean {
    const list = this.paginatedMaintenanceGroups;
    if (!list.length || !this.isInheritedOnlyGroup(list[0])) {
      return false;
    }
    return index === 0;
  }

  showOwnMaintenanceSectionHeader(
    group: ModuleGroup<MaintenanceItem>,
    index: number
  ): boolean {
    if (this.isInheritedOnlyGroup(group)) {
      return false;
    }
    const list = this.paginatedMaintenanceGroups;
    const hasInheritedSection = list.some((g) => this.isInheritedOnlyGroup(g));
    if (!hasInheritedSection) {
      return false;
    }
    const firstOwn = list.findIndex((g) => !this.isInheritedOnlyGroup(g));
    return firstOwn === index;
  }

  private normalizeRequirementsResponse(result: any): RequirementItem[] {
    let items: any[] = [];
    if (result?.data && Array.isArray(result.data)) items = result.data;
    else if (result?.requirements && Array.isArray(result.requirements))
      items = result.requirements;
    else if (result?.items && Array.isArray(result.items)) items = result.items;
    return items.map((item: any) => {
      const name = item.item_details?.name || item.name || 'Unnamed';
      const start =
        item.start_date ??
        item.version_details?.valid_from ??
        item.version_details?.start_date ??
        item.created_at ??
        '';
      const end =
        item.end_date ??
        item.version_details?.valid_to ??
        item.version_details?.end_date ??
        item.updated_at ??
        '';
      const isSubtype = !!item.subtype_id;
      return {
        id: item.id,
        module_id: item.module_id ?? null,
        subtype_id: item.subtype_id ?? null,
        name,
        type:
          item.item_details?.type ||
          (isSubtype ? 'Subtype' : 'Module') ||
          item.type ||
          'unknown',
        start_date: start || null,
        end_date: end || null,
        itemDescription:
          this.extractItemDescriptionFromApi(item.version_details) ??
          this.extractItemDescriptionFromApi(item.item_details),
      };
    });
  }

  private extractItemDescriptionFromApi(details: any): string | undefined {
    if (!details || typeof details !== 'object') return undefined;
    const raw = (details as any).description;
    if (typeof raw === 'string' && raw.trim())
      return this.normalizeDescriptionText(raw);
    const descs = (details as any).descriptions;
    if (descs && typeof descs === 'object' && !Array.isArray(descs)) {
      for (const lang of ['da', 'da_DK', 'en']) {
        const block = (descs as any)[lang];
        const text =
          block?.translation_text ?? (typeof block === 'string' ? block : null);
        if (text && String(text).trim())
          return this.normalizeDescriptionText(String(text));
      }
      for (const v of Object.values(descs)) {
        if (v && typeof v === 'object' && (v as any).translation_text != null) {
          const t = String((v as any).translation_text).trim();
          if (t) return this.normalizeDescriptionText(t);
        }
      }
    }
    if (Array.isArray(descs)) {
      for (const block of descs) {
        const t = block?.translation_text ?? block?.text;
        if (t && String(t).trim())
          return this.normalizeDescriptionText(String(t));
      }
    }
    return undefined;
  }

  private normalizeDescriptionText(html: string): string {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.functionRegistryService.registerFunction(
      'refreshEducationalPathModules',
      () => {}
    );
  }

  loadMaintenance(): void {
    if (!this.versionId) return;
    this.loadingMaintenance = true;
    this.cdr.markForCheck();
    this.http
      .get<any>(
        `${environment.apiUrl}/paths/versions/${this.versionId}/maintenance`,
        {
          params: { pageSize: '500', page: '1' },
          withCredentials: true,
        }
      )
      .pipe(catchError(() => of({ data: [], items: [] })))
      .subscribe((res) => {
        this.maintenanceItems = this.normalizeMaintenanceResponse(res);
        this._maintenanceDirty = true;
        this.loadingMaintenance = false;
        this.cdr.markForCheck();
      });
  }

  loadRequirements(): void {
    if (!this.versionId) return;
    this.loadingRequirements = true;
    this.cdr.markForCheck();
    this.http
      .get<any>(
        `${environment.apiUrl}/paths/versions/${this.versionId}/requirements`,
        {
          params: { pageSize: '500', page: '1' },
          withCredentials: true,
        }
      )
      .pipe(catchError(() => of({ data: [], requirements: [] })))
      .subscribe((res) => {
        this.requirementItems = this.normalizeRequirementsResponse(res);
        this._requirementsDirty = true;
        this.loadingRequirements = false;
        this.cdr.markForCheck();
      });
  }

  // ─── Grouping ────────────────────────────────────────────────────────────────

  private itemOverlapsPeriod(
    itemStart: string | null | undefined,
    itemEnd: string | null | undefined,
    periodStart: Date,
    periodEnd: Date
  ): boolean {
    const s = itemStart ? new Date(itemStart).getTime() : 0;
    const e = itemEnd ? new Date(itemEnd).getTime() : Number.MAX_SAFE_INTEGER;
    if (isNaN(s) || isNaN(e)) return false;
    return s <= periodEnd.getTime() && e >= periodStart.getTime();
  }

  private syncViewRollBounds(): void {
    if (!this.versionRecurrenceInput) {
      this.currentPeriodStart = null;
      this.currentPeriodEnd = null;
      return;
    }
    const bounds = computeMaintenanceRollBounds(
      this.versionRecurrenceInput,
      profileYearToReferenceDate(this.selectedModuleYear)
    );
    if (!bounds) {
      this.currentPeriodStart = null;
      this.currentPeriodEnd = null;
      return;
    }
    this.currentPeriodStart = bounds.start;
    this.currentPeriodEnd = bounds.end;
  }

  private selectedRollBounds(): { start: Date; end: Date } {
    if (this.currentPeriodStart && this.currentPeriodEnd) {
      return { start: this.currentPeriodStart, end: this.currentPeriodEnd };
    }
    const y = this.selectedModuleYear;
    return {
      start: new Date(y, 0, 1),
      end: new Date(y, 11, 31, 23, 59, 59, 999),
    };
  }

  get viewRollYearsLabel(): string | null {
    if (
      !this.versionRecurrenceInput ||
      !isMultiYearRoll(
        this.versionRecurrenceInput.recurrence_value,
        this.versionRecurrenceInput.recurrence_unit
      ) ||
      !this.currentPeriodStart ||
      !this.currentPeriodEnd
    ) {
      return null;
    }
    return formatRollYearsLabel({
      start: this.currentPeriodStart,
      end: this.currentPeriodEnd,
    });
  }

  get viewRollSummaryLabel(): string {
    if (this.viewRollYearsLabel) {
      return `Rul ${this.viewRollYearsLabel}`;
    }
    return `År ${this.selectedModuleYear}`;
  }

  /** Years that appear on any maintenance or requirement period (for the dropdown). */
  get moduleYearOptions(): number[] {
    const years = new Set<number>();
    const cy = new Date().getFullYear();
    years.add(cy);
    this.addYearsFromItemList(this.maintenanceItems, years);
    this.addYearsFromItemList(this.requirementItems, years);
    years.add(this.selectedModuleYear);
    return Array.from(years).sort((a, b) => b - a);
  }

  private addYearsFromItemList(
    items: Array<{ start_date?: string | null; end_date?: string | null }>,
    years: Set<number>
  ): void {
    items.forEach((e) => {
      if (!e.start_date && !e.end_date) return;
      const s = e.start_date ? new Date(e.start_date).getTime() : NaN;
      const en = e.end_date ? new Date(e.end_date).getTime() : NaN;
      if (isNaN(s) && isNaN(en)) return;
      const t0 = !isNaN(s) ? s : en;
      const t1 = !isNaN(en) ? en : s;
      let y1 = new Date(t0).getFullYear();
      let y2 = new Date(t1).getFullYear();
      if (y1 > y2) [y1, y2] = [y2, y1];
      for (let y = y1; y <= y2; y++) years.add(y);
    });
  }

  onModuleYearChange(year: number): void {
    this.selectedModuleYear = year;
    this.syncViewRollBounds();
    this._maintenanceDirty = true;
    this._requirementsDirty = true;
    this.maintenanceGroupsPageIndex = 0;
    this.requirementsGroupsPageIndex = 0;
    this.cdr.markForCheck();
  }

  private sortModuleNames(a: string, b: string): number {
    return a.localeCompare(b, 'da', { sensitivity: 'base', numeric: true });
  }

  private itemGroupKey(item: { name?: string; itemName?: string }): string {
    return (item as any).itemName ?? item.name ?? 'Unnamed';
  }

  isActiveInCurrentPeriod(
    start: string | null | undefined,
    end: string | null | undefined
  ): boolean {
    if (!this.currentPeriodStart || !this.currentPeriodEnd) return false;
    return this.itemOverlapsPeriod(
      start,
      end,
      this.currentPeriodStart,
      this.currentPeriodEnd
    );
  }

  private buildCoverageYears(
    entries: Array<{ start_date?: string | null; end_date?: string | null }>
  ): CoverageYear[] {
    if (!entries.length) return [];
    let minYear = Infinity,
      maxYear = -Infinity;
    entries.forEach((e) => {
      if (e.start_date) {
        const y = new Date(e.start_date).getFullYear();
        if (!isNaN(y)) {
          minYear = Math.min(minYear, y);
          maxYear = Math.max(maxYear, y);
        }
      }
      if (e.end_date) {
        const y = new Date(e.end_date).getFullYear();
        if (!isNaN(y)) {
          minYear = Math.min(minYear, y);
          maxYear = Math.max(maxYear, y);
        }
      }
    });
    if (!isFinite(minYear)) return [];
    const result: CoverageYear[] = [];
    for (let y = minYear; y <= maxYear; y++) {
      const ps = new Date(y, 0, 1);
      const pe = new Date(y, 11, 31, 23, 59, 59, 999);
      const covered = entries.some((e) =>
        this.itemOverlapsPeriod(e.start_date, e.end_date, ps, pe)
      );
      result.push({
        year: y,
        covered,
        isCurrent: y === this.selectedModuleYear,
      });
    }
    return result;
  }

  /**
   * Collapse consecutive years with the same coverage into ranges.
   * The selected filter year and the real calendar year always stay as their own chips.
   */
  private compactCoverageYears(years: CoverageYear[]): CoverageSegment[] {
    if (!years.length) return [];
    const calendarYear = new Date().getFullYear();
    const selectedYear = this.selectedModuleYear;
    const segments: CoverageSegment[] = [];

    let i = 0;
    while (i < years.length) {
      const y = years[i];
      const isolate = y.year === calendarYear || y.year === selectedYear;
      if (isolate) {
        segments.push(
          this.makeCoverageSegment(
            y.year,
            y.year,
            y.covered,
            y.year === selectedYear,
            y.year === calendarYear
          )
        );
        i++;
        continue;
      }

      const start = y.year;
      const covered = y.covered;
      let end = y.year;
      while (i + 1 < years.length) {
        const next = years[i + 1];
        if (next.year !== end + 1 || next.covered !== covered) break;
        if (next.year === calendarYear || next.year === selectedYear) break;
        end = next.year;
        i++;
      }
      segments.push(
        this.makeCoverageSegment(start, end, covered, false, false)
      );
      i++;
    }
    return segments;
  }

  private makeCoverageSegment(
    startYear: number,
    endYear: number,
    covered: boolean,
    isCurrent: boolean,
    isCalendarYear: boolean
  ): CoverageSegment {
    const label =
      startYear === endYear ? String(startYear) : `${startYear}–${endYear}`;
    const status = covered ? 'dækket' : 'mangler dækning';
    const count = endYear - startYear + 1;
    let title =
      startYear === endYear
        ? `${startYear}: ${status}`
        : `${startYear}–${endYear} (${count} år): ${status}`;
    if (isCalendarYear) title += ' · nuværende år';
    else if (isCurrent) title += ' · valgt år';
    return {
      key: `${startYear}-${endYear}-${covered ? 1 : 0}`,
      label,
      covered,
      isCurrent,
      isCalendarYear,
      title,
      startYear,
      endYear,
    };
  }

  private buildModuleGroups<
    T extends {
      name?: string;
      itemName?: string;
      type: string;
      itemType?: string;
      module_id?: number | null;
      subtype_id?: number | null;
      start_date?: string | null;
      end_date?: string | null;
    }
  >(items: T[]): ModuleGroup<T>[] {
    const { start: yearStart, end: yearEnd } = this.selectedRollBounds();
    let list = items.filter((row) =>
      this.itemOverlapsPeriod(row.start_date, row.end_date, yearStart, yearEnd)
    );
    if (
      this._filterActiveOnly &&
      this.currentPeriodStart &&
      this.currentPeriodEnd
    ) {
      list = list.filter((row) =>
        this.isActiveInCurrentPeriod(row.start_date, row.end_date)
      );
    }

    const map = new Map<string, ModuleGroup<T>>();
    list.forEach((item) => {
      const name = this.itemGroupKey(item);
      const key = name;
      if (!map.has(key)) {
        const isSubtype = !!item.subtype_id;
        map.set(key, {
          key,
          name,
          type: item.type,
          itemType: item.itemType,
          sourceId: item.module_id ?? item.subtype_id ?? null,
          isSubtype,
          entries: [],
          hasCurrentPeriodCoverage: false,
          coverageDisplay: [],
        });
      }
      map.get(key)!.entries.push(item);
    });

    const groups = Array.from(map.values());
    groups.forEach((g) => {
      g.entries.sort((a, b) => {
        const da = a.start_date ? new Date(a.start_date).getTime() : 0;
        const db = b.start_date ? new Date(b.start_date).getTime() : 0;
        return da - db;
      });
      const allForKey = items.filter(
        (item) => this.itemGroupKey(item) === g.key
      );
      g.hasCurrentPeriodCoverage = allForKey.some((e) =>
        this.isActiveInCurrentPeriod(e.start_date, e.end_date)
      );
      g.coverageDisplay = this.compactCoverageYears(
        this.buildCoverageYears(allForKey)
      );
    });

    groups.sort((a, b) => this.sortModuleNames(a.name, b.name));
    return groups;
  }

  get maintenanceByModule(): ModuleGroup<MaintenanceItem>[] {
    if (!this._maintenanceDirty) return this._maintenanceCache;
    this._maintenanceCache = this.buildModuleGroups(this.maintenanceItems);
    this._maintenanceDirty = false;
    return this._maintenanceCache;
  }

  get requirementsByModule(): ModuleGroup<RequirementItem>[] {
    if (!this._requirementsDirty) return this._requirementsCache;
    this._requirementsCache = this.buildModuleGroups(this.requirementItems);
    this._requirementsDirty = false;
    return this._requirementsCache;
  }

  // ─── Edit existing ────────────────────────────────────────────────────────────

  openEditMaintenance(row: MaintenanceItem, groupKey?: string): void {
    if (this.isInheritedMaintenance(row)) {
      return;
    }
    this.editingMaintenanceId = row.id;
    this.editForm = {
      start_date: row.start_date || '',
      end_date: row.end_date || '',
      required: row.required ?? true,
    };
    if (groupKey) this.ensureExpanded(groupKey, this.expandedMaintenance);
    else this.cdr.markForCheck();
  }

  cancelEditMaintenance(): void {
    this.editingMaintenanceId = null;
    this.cdr.markForCheck();
  }

  saveEditMaintenance(): void {
    if (!this.versionId || this.editingMaintenanceId == null) return;
    const body: any = {};
    if (this.editForm.start_date) body.start_date = this.editForm.start_date;
    if (this.editForm.end_date) body.end_date = this.editForm.end_date;
    body.required = this.editForm.required;
    this.http
      .patch(
        `${environment.apiUrl}/paths/versions/${this.versionId}/maintenance/${this.editingMaintenanceId}`,
        body,
        { withCredentials: true }
      )
      .subscribe({
        next: () => {
          this.editingMaintenanceId = null;
          this.loadMaintenance();
        },
        error: (err) => console.error('PATCH maintenance failed', err),
      });
  }

  openEditRequirement(row: RequirementItem, groupKey?: string): void {
    this.editingRequirementId = row.id;
    this.editRequirementForm = {
      start_date: row.start_date || '',
      end_date: row.end_date || '',
    };
    if (groupKey) this.ensureExpanded(groupKey, this.expandedRequirements);
    else this.cdr.markForCheck();
  }

  cancelEditRequirement(): void {
    this.editingRequirementId = null;
    this.cdr.markForCheck();
  }

  saveEditRequirement(): void {
    if (!this.versionId || this.editingRequirementId == null) return;
    const body: any = {};
    if (this.editRequirementForm.start_date)
      body.start_date = this.editRequirementForm.start_date;
    if (this.editRequirementForm.end_date)
      body.end_date = this.editRequirementForm.end_date;
    this.http
      .patch(
        `${environment.apiUrl}/paths/versions/${this.versionId}/requirements/${this.editingRequirementId}`,
        body,
        { withCredentials: true }
      )
      .subscribe({
        next: () => {
          this.editingRequirementId = null;
          this.loadRequirements();
        },
        error: (err) => console.error('PATCH requirement failed', err),
      });
  }

  removeMaintenance(row: MaintenanceItem): void {
    if (!this.versionId || row?.id == null || this.isInheritedMaintenance(row)) {
      return;
    }
    this.http
      .delete(
        `${environment.apiUrl}/paths/versions/${this.versionId}/maintenance/${row.id}`,
        { withCredentials: true }
      )
      .subscribe({
        next: () => this.loadMaintenance(),
        error: (err) => console.error('removeMaintenance failed', err),
      });
  }

  removeRequirement(row: RequirementItem): void {
    if (!this.versionId || row?.id == null) return;
    this.http
      .delete(
        `${environment.apiUrl}/paths/versions/${this.versionId}/requirements/${row.id}`,
        { withCredentials: true }
      )
      .subscribe({
        next: () => this.loadRequirements(),
        error: (err) => console.error('removeRequirement failed', err),
      });
  }

  // ─── Inline "add period for this module" ─────────────────────────────────────

  openInlineAddMaintenance(group: ModuleGroup<MaintenanceItem>): void {
    this.addingPeriodForKey = group.key;
    this.ensureExpanded(group.key, this.expandedMaintenance);
    const lastEntry = group.entries[group.entries.length - 1];
    this.inlineAddForm = {
      start_date: '',
      end_date: '',
      required: lastEntry?.required ?? true,
    };
    this.cdr.markForCheck();
  }

  cancelInlineAddMaintenance(): void {
    this.addingPeriodForKey = null;
    this.cdr.markForCheck();
  }

  saveInlineAddMaintenance(group: ModuleGroup<MaintenanceItem>): void {
    if (
      !this.versionId ||
      !this.inlineAddForm.start_date ||
      group.sourceId == null
    )
      return;
    this.savingInlineAdd = true;
    const item = group.isSubtype
      ? {
          subtype_id: group.sourceId,
          module_id: null,
          required: this.inlineAddForm.required,
          start_date: this.inlineAddForm.start_date,
          end_date: this.inlineAddForm.end_date || null,
        }
      : {
          module_id: group.sourceId,
          subtype_id: null,
          required: this.inlineAddForm.required,
          start_date: this.inlineAddForm.start_date,
          end_date: this.inlineAddForm.end_date || null,
        };
    this.http
      .post(
        `${environment.apiUrl}/paths/versions/${this.versionId}/maintenance`,
        { items: [item] },
        { withCredentials: true }
      )
      .subscribe({
        next: () => {
          this.addingPeriodForKey = null;
          this.savingInlineAdd = false;
          this.loadMaintenance();
        },
        error: (err) => {
          console.error('Inline add maintenance failed', err);
          this.savingInlineAdd = false;
          this.cdr.markForCheck();
        },
      });
  }

  openInlineAddRequirement(group: ModuleGroup<RequirementItem>): void {
    this.addingReqPeriodForKey = group.key;
    this.ensureExpanded(group.key, this.expandedRequirements);
    this.inlineAddReqForm = { start_date: '', end_date: '' };
    this.cdr.markForCheck();
  }

  cancelInlineAddRequirement(): void {
    this.addingReqPeriodForKey = null;
    this.cdr.markForCheck();
  }

  saveInlineAddRequirement(group: ModuleGroup<RequirementItem>): void {
    if (
      !this.versionId ||
      !this.inlineAddReqForm.start_date ||
      group.sourceId == null
    )
      return;
    this.savingInlineAddReq = true;
    const req = {
      item_type: group.isSubtype ? 'Subtype' : 'Module',
      item_id: group.sourceId,
      version_start_date: this.inlineAddReqForm.start_date,
      version_end_date: this.inlineAddReqForm.end_date || undefined,
    };
    this.http
      .post(
        `${environment.apiUrl}/paths/versions/${this.versionId}/requirements`,
        { requirements: [req] },
        { withCredentials: true }
      )
      .subscribe({
        next: () => {
          this.addingReqPeriodForKey = null;
          this.savingInlineAddReq = false;
          this.loadRequirements();
        },
        error: (err) => {
          console.error('Inline add requirement failed', err);
          this.savingInlineAddReq = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ─── Open full add modal ──────────────────────────────────────────────────────

  onAddMaintenance(): void {
    this.functionRegistryService.executeFunction(
      'openModal',
      'edit_path_maintenance'
    );
  }

  onAddRequirement(): void {
    this.functionRegistryService.executeFunction(
      'openModal',
      'edit_path_requirements'
    );
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  getName(item: MaintenanceItem | RequirementItem): string {
    return (item as any).itemName ?? (item as any).name ?? '–';
  }

  formatDateRange(
    start: string | null | undefined,
    end: string | null | undefined
  ): string {
    if (!start && !end) return 'Ingen datoer';
    const s = start
      ? new Date(start).toLocaleDateString('da-DK', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '?';
    const e = end
      ? new Date(end).toLocaleDateString('da-DK', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '?';
    return `${s} – ${e}`;
  }

  formatPeriodRange(): string {
    if (!this.currentPeriodStart || !this.currentPeriodEnd) return '';
    return `${this.currentPeriodStart.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })} – ${this.currentPeriodEnd.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })}`;
  }

  /** Minutes as "X timer Y min" (or only minutes when under one hour). */
  formatMaintenanceMinutes(totalMinutes: number): string {
    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '0 timer';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes} min`;
    const hourLabel = `${hours} ${hours === 1 ? 'time' : 'timer'}`;
    if (minutes === 0) return hourLabel;
    return `${hourLabel} ${minutes} min`;
  }

  /**
   * Minutes for the collapsed header: first period row in the filtered year (group.entries are already
   * filtered to selectedModuleYear). Each row carries maintenance_minutes for its period-overlapping module version.
   */
  maintenanceHeaderMinutesLabelForYearFilter(
    group: ModuleGroup<MaintenanceItem>
  ): string | null {
    for (const e of group.entries) {
      const m = e.maintenance_minutes;
      if (m != null && typeof m === 'number' && !Number.isNaN(m))
        return `${m} min`;
    }
    return null;
  }

  /** Påkrævet / Valgfri for periods in the filtered year (mixed if both exist). */
  maintenanceHeaderRequiredDisplay(
    group: ModuleGroup<MaintenanceItem>
  ): { text: string; kind: 'req' | 'opt' | 'mixed' } | null {
    if (!group.entries.length) return null;
    let anyRequired = false;
    let anyOptional = false;
    for (const e of group.entries) {
      if (e.required === false) anyOptional = true;
      else anyRequired = true;
    }
    if (anyRequired && anyOptional)
      return { text: 'Påkrævet og valgfri', kind: 'mixed' };
    if (anyOptional) return { text: 'Valgfri', kind: 'opt' };
    return { text: 'Påkrævet', kind: 'req' };
  }

  groupItemDescriptionText(
    group: ModuleGroup<{ itemDescription?: string }>
  ): string | null {
    for (const e of group.entries) {
      const d = e.itemDescription?.trim();
      if (d) return d;
    }
    return null;
  }

  // ─── Search + expand helpers ──────────────────────────────────────────────

  private filterBySearch<T>(
    groups: ModuleGroup<T>[],
    query: string
  ): ModuleGroup<T>[] {
    if (!query.trim()) return groups;
    const q = query.trim().toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.itemType || g.type).toLowerCase().includes(q)
    );
  }

  get filteredMaintenanceGroups(): ModuleGroup<MaintenanceItem>[] {
    const groups = this.filterBySearch(
      this.maintenanceByModule,
      this.maintenanceSearch
    );
    return [...groups].sort((a, b) => {
      const aInheritedOnly = this.isInheritedOnlyGroup(a) ? 0 : 1;
      const bInheritedOnly = this.isInheritedOnlyGroup(b) ? 0 : 1;
      if (aInheritedOnly !== bInheritedOnly) {
        return aInheritedOnly - bInheritedOnly;
      }
      return this.sortModuleNames(a.name, b.name);
    });
  }

  get filteredRequirementsGroups(): ModuleGroup<RequirementItem>[] {
    return this.filterBySearch(
      this.requirementsByModule,
      this.requirementsSearch
    );
  }

  get paginatedMaintenanceGroups(): ModuleGroup<MaintenanceItem>[] {
    const list = this.filteredMaintenanceGroups;
    const start =
      this.maintenanceGroupsPageIndex * this.maintenanceGroupsPageSize;
    return list.slice(start, start + this.maintenanceGroupsPageSize);
  }

  get paginatedRequirementsGroups(): ModuleGroup<RequirementItem>[] {
    const list = this.filteredRequirementsGroups;
    const start =
      this.requirementsGroupsPageIndex * this.requirementsGroupsPageSize;
    return list.slice(start, start + this.requirementsGroupsPageSize);
  }

  get maintenanceGroupsTotalPages(): number {
    const total = this.filteredMaintenanceGroups.length;
    return total <= 0 ? 1 : Math.ceil(total / this.maintenanceGroupsPageSize);
  }

  get requirementsGroupsTotalPages(): number {
    const total = this.filteredRequirementsGroups.length;
    return total <= 0 ? 1 : Math.ceil(total / this.requirementsGroupsPageSize);
  }

  get maintenanceActiveCount(): number {
    return this.maintenanceByModule.filter((g) => g.hasCurrentPeriodCoverage)
      .length;
  }

  get requirementsActiveCount(): number {
    return this.requirementsByModule.filter((g) => g.hasCurrentPeriodCoverage)
      .length;
  }

  onMaintenanceSearchChange(): void {
    this.maintenanceGroupsPageIndex = 0;
    if (this.maintenanceSearch.trim()) {
      // Auto-expand all matching when searching
      this.filteredMaintenanceGroups.forEach((g) =>
        this.expandedMaintenance.add(g.key)
      );
    } else {
      // Collapse all when search is cleared
      this.expandedMaintenance.clear();
    }
  }

  onRequirementsSearchChange(): void {
    this.requirementsGroupsPageIndex = 0;
    if (this.requirementsSearch.trim()) {
      this.filteredRequirementsGroups.forEach((g) =>
        this.expandedRequirements.add(g.key)
      );
    } else {
      this.expandedRequirements.clear();
    }
  }

  toggleExpand(key: string, set: Set<string>): void {
    if (set.has(key)) set.delete(key);
    else set.add(key);
    this.cdr.markForCheck();
  }

  expandAll(groups: ModuleGroup<any>[], set: Set<string>): void {
    groups.forEach((g) => set.add(g.key));
    this.cdr.markForCheck();
  }

  collapseAll(set: Set<string>): void {
    set.clear();
    this.cdr.markForCheck();
  }

  isExpanded(key: string, set: Set<string>): boolean {
    return set.has(key);
  }

  // If the user opens inline add or edit, expand the card automatically
  private ensureExpanded(key: string, set: Set<string>): void {
    set.add(key);
    this.cdr.markForCheck();
  }

  trackByKey(_: number, group: ModuleGroup<any>): string {
    return group.key;
  }
  trackById(_: number, item: any): number {
    return item.id;
  }
  trackByCoverageSegment(_: number, seg: CoverageSegment): string {
    return seg.key;
  }

  /** Per-group sums; entries already filtered to the selected year (and active-only if enabled). */
  private maintenanceYearTotals(group: ModuleGroup<MaintenanceItem>): {
    required: number;
    optional: number;
    total: number;
  } {
    let required = 0;
    let optional = 0;
    for (const row of group.entries) {
      const m = row.maintenance_minutes;
      if (m == null || typeof m !== 'number' || Number.isNaN(m)) continue;
      if (row.required === false) optional += m;
      else required += m;
    }
    return { required, optional, total: required + optional };
  }

  /**
   * Aggregated maintenance minutes for all groups matching year, active filter, and search (all pages).
   * Null when there are no matching groups (hides the summary row).
   */
  get maintenanceFilteredYearTotals(): {
    required: number;
    optional: number;
    total: number;
  } | null {
    const groups = this.filteredMaintenanceGroups;
    if (!groups.length) return null;
    let required = 0;
    let optional = 0;
    for (const g of groups) {
      const t = this.maintenanceYearTotals(g);
      required += t.required;
      optional += t.optional;
    }
    return { required, optional, total: required + optional };
  }
}
